/**
 * Copy for the public signing page. Kept local to the page rather than in the
 * global i18n bundle because this flow is standalone (no app chrome) and its
 * wording is legal-adjacent — it should be reviewed as one unit.
 *
 * The agreement document itself is always rendered in Dutch; only the guidance
 * around it is translated.
 */
const translations = {
  nl: {
    brandTagline: "Beveiligde ondertekening",
    documentTitle: "Klantaansprakelijkheid",

    loading: "Bezig met laden…",
    invalidTitle: "Deze link is niet geldig",
    invalidBody:
      "Controleer of u de volledige link uit de e-mail heeft geopend. Neem contact met ons op als het probleem aanhoudt.",

    lockedTitle: "Tijdelijk geblokkeerd",
    lockedBody:
      "Er zijn te veel mislukte pogingen gedaan. Probeer het later opnieuw of neem contact op met Satyam Holidays.",

    alreadySignedTitle: "Al ondertekend",
    alreadySignedBody:
      "Deze overeenkomst is al ondertekend. U heeft een kopie per e-mail ontvangen.",

    // Step 1 — passport
    step1: "Identiteit",
    step2: "Foto",
    step3: "Lezen en ondertekenen",
    step4: "Klaar",

    unlockHeading: "Bevestig uw identiteit",
    unlockGreeting:
      "Hallo {{name}}, u staat op het punt een overeenkomst te ondertekenen.",
    unlockIntro:
      "Om deze overeenkomst te openen uploadt u een foto van uw paspoort. Zo weten wij zeker dat alleen u dit document kunt lezen en ondertekenen.",
    unlockCta: "Paspoort uploaden",
    unlockCtaCamera: "Foto maken met camera",
    unlockChecking: "Paspoort wordt gecontroleerd…",
    unlockChecklistTitle: "Zorg dat:",
    unlockCheck1:
      "de MRZ-code onderaan (de twee regels met «-tekens) volledig zichtbaar is;",
    unlockCheck2: "de hele pagina scherp en goed verlicht is;",
    unlockCheck3: "er geen vingers of schaduwen over de tekst vallen.",
    unlockWarningTitle: "Upload alleen uw eigen paspoort",
    unlockWarningBody:
      "Upload uitsluitend het paspoort van de persoon die de betaling verricht. Bij een afwijking kunnen wij uw betaling niet verder verwerken.",
    unlockAttempts: "Nog {{count}} poging(en) over.",
    unlockPrivacy:
      "Uw paspoort wordt uitsluitend gebruikt om uw identiteit te controleren en versleuteld bewaard bij uw overeenkomst.",
    fileTooLarge: "Dit bestand is groter dan 15 MB. Kies een kleiner bestand.",
    fileWrongType: "Upload een JPG-, PNG-, HEIC-afbeelding of een PDF.",

    // Step 2 — selfie
    selfieHeading: "Maak een foto van uzelf",
    selfieIntro:
      "Onze medewerker vergelijkt deze live foto met de foto in uw paspoort. Zo weten wij zeker dat u degene bent die betaalt. Uploaden van een bestaande foto is niet mogelijk.",
    selfiePlaceholder: "Uw camera is nog niet gestart.",
    selfieStarting: "Camera wordt gestart…",
    selfieStartCta: "Camera starten",
    selfieShootCta: "Foto maken",
    selfieUseCta: "Deze foto gebruiken",
    selfieRetakeCta: "Opnieuw maken",
    selfieUploading: "Foto wordt verstuurd…",
    selfieCameraBlocked:
      "Wij kunnen uw camera niet gebruiken. Geef toestemming in uw browserinstellingen en probeer het opnieuw. Een live foto is verplicht.",
    selfieChecklistTitle: "Zorg dat:",
    selfieCheck1: "uw gezicht volledig zichtbaar is en recht in beeld;",
    selfieCheck2: "u geen zonnebril, pet of mondkapje draagt;",
    selfieCheck3: "de foto scherp is en goed verlicht.",
    selfiePrivacy:
      "Deze foto wordt uitsluitend gebruikt om uw identiteit te controleren en versleuteld bewaard bij uw overeenkomst.",

    // Step 2 — document
    reviewHeading: "Lees de overeenkomst",
    reviewIntro:
      "Neem het document rustig door. U kunt pas ondertekenen nadat u tot het einde heeft gescrold.",
    verifiedAs: "Geverifieerd als {{name}}",
    scrollHint: "Scrol naar beneden om verder te lezen",
    scrollDone: "U heeft het hele document gelezen",
    scrollToBottom: "Naar het einde",
    amountLabel: "Te betalen bedrag",
    serviceLabel: "Dienst",
    referenceLabel: "Referentie",

    // Signature
    signHeading: "Plaats uw handtekening",
    signDrawHint:
      "Teken uw handtekening met uw vinger of muis in het vak. Gebruik dezelfde handtekening als in uw paspoort.",
    signClear: "Wissen",
    signUndo: "Ongedaan maken",
    consentLabel:
      "Ik heb dit document gelezen, begrepen en ga akkoord met alle voorwaarden. Ik ga ermee akkoord dat mijn elektronische handtekening dezelfde rechtsgeldigheid heeft als een handgeschreven handtekening.",
    signCta: "Ondertekenen",
    signing: "Bezig met ondertekenen…",
    signErrorScroll: "Lees eerst het volledige document.",
    signErrorConsent: "Vink het akkoord aan om te kunnen ondertekenen.",
    signErrorEmpty: "Plaats eerst uw handtekening.",

    // Step 3 — done
    doneHeading: "Bedankt, uw overeenkomst is ondertekend",
    doneBody:
      "Onze medewerker controleert nu uw gegevens en stuurt u daarna de betaallink.",
    doneEmailTitle: "Kopie per e-mail verstuurd",
    doneEmailBody: "Een ondertekende kopie is ook naar {{email}} gestuurd.",
    doneNextTitle: "Wat gebeurt er nu?",
    doneNext1: "Wij controleren uw paspoort en handtekening.",
    doneNext2: "U ontvangt de Stripe-betaallink van uw contactpersoon.",
    doneNext3: "Na betaling bevestigen wij uw reservering definitief.",

    retry: "Opnieuw proberen",
    needHelp: "Hulp nodig? Bel",
  },

  en: {
    brandTagline: "Secure signing",
    documentTitle: "Customer Liability",

    loading: "Loading…",
    invalidTitle: "This link is not valid",
    invalidBody:
      "Please check that you opened the complete link from your email. Contact us if the problem continues.",

    lockedTitle: "Temporarily locked",
    lockedBody:
      "Too many failed attempts were made. Please try again later or contact Satyam Holidays.",

    alreadySignedTitle: "Already signed",
    alreadySignedBody:
      "This agreement has already been signed. A copy was emailed to you.",

    step1: "Identity",
    step2: "Photo",
    step3: "Read and sign",
    step4: "Done",

    unlockHeading: "Confirm your identity",
    unlockGreeting: "Hello {{name}}, you are about to sign an agreement.",
    unlockIntro:
      "To open this agreement, upload a photo of your passport. This makes sure only you can read and sign this document.",
    unlockCta: "Upload passport",
    unlockCtaCamera: "Take a photo with camera",
    unlockChecking: "Checking your passport…",
    unlockChecklistTitle: "Make sure that:",
    unlockCheck1:
      "the MRZ code at the bottom (the two lines with « characters) is fully visible;",
    unlockCheck2: "the whole page is sharp and well lit;",
    unlockCheck3: "no fingers or shadows cover the text.",
    unlockWarningTitle: "Only upload your own passport",
    unlockWarningBody:
      "Upload only the passport of the person who will make the payment. If it does not match, we cannot process your payment.",
    unlockAttempts: "{{count}} attempt(s) remaining.",
    unlockPrivacy:
      "Your passport is used solely to verify your identity and is stored securely with your agreement.",
    fileTooLarge:
      "This file is larger than 15 MB. Please choose a smaller one.",
    fileWrongType: "Please upload a JPG, PNG or HEIC image, or a PDF.",

    selfieHeading: "Take a photo of yourself",
    selfieIntro:
      "Our colleague compares this live photo with the one in your passport, so we can be sure you are the person making the payment. Uploading an existing photo is not possible.",
    selfiePlaceholder: "Your camera has not started yet.",
    selfieStarting: "Starting camera…",
    selfieStartCta: "Start camera",
    selfieShootCta: "Take photo",
    selfieUseCta: "Use this photo",
    selfieRetakeCta: "Retake",
    selfieUploading: "Sending photo…",
    selfieCameraBlocked:
      "We cannot access your camera. Allow it in your browser settings and try again. A live photo is required.",
    selfieChecklistTitle: "Make sure that:",
    selfieCheck1: "your face is fully visible and facing the camera;",
    selfieCheck2: "you are not wearing sunglasses, a cap or a face mask;",
    selfieCheck3: "the photo is sharp and well lit.",
    selfiePrivacy:
      "This photo is used solely to verify your identity and is stored securely with your agreement.",

    reviewHeading: "Read the agreement",
    reviewIntro:
      "Please take your time. You can sign once you have scrolled to the end.",
    verifiedAs: "Verified as {{name}}",
    scrollHint: "Scroll down to keep reading",
    scrollDone: "You have read the whole document",
    scrollToBottom: "Jump to the end",
    amountLabel: "Amount due",
    serviceLabel: "Service",
    referenceLabel: "Reference",

    signHeading: "Add your signature",
    signDrawHint:
      "Draw your signature in the box with your finger or mouse. Use the same signature as in your passport.",
    signClear: "Clear",
    signUndo: "Undo",
    consentLabel:
      "I have read and understood this document and accept all its terms. I agree that my electronic signature has the same legal effect as a handwritten signature.",
    signCta: "Sign the agreement",
    signing: "Signing…",
    signErrorScroll: "Please read the whole document first.",
    signErrorConsent: "Tick the box to confirm you accept the terms.",
    signErrorEmpty: "Please add your signature first.",

    doneHeading: "Thank you, your agreement is signed",
    doneBody:
      "Our colleague will now verify your details and send you the payment link.",
    doneEmailTitle: "Copy sent by email",
    doneEmailBody:
      "A signed copy has also been sent to {{email}}. You do not need to download the document here.",
    doneNextTitle: "What happens next?",
    doneNext1: "We verify your passport and signature.",
    doneNext2: "Your contact sends you the Stripe payment link.",
    doneNext3: "Once paid, we confirm your booking definitively.",

    retry: "Try again",
    needHelp: "Need help? Call",
  },
};

export const createTranslator = (locale) => {
  const dictionary = translations[locale] || translations.nl;
  return (key, params) => {
    const value = dictionary[key] ?? translations.nl[key] ?? key;
    if (!params) return value;
    return Object.keys(params).reduce(
      (text, param) => text.replaceAll(`{{${param}}}`, params[param]),
      value,
    );
  };
};

export default translations;
